import React, { useState, useEffect, useContext, Fragment } from 'react';
import {
  Container,
  Button,
  Label,
  Form,
  Input as RInput,
  FormGroup,
  Spinner,
  Table,
  Row,
  Col,
  Alert,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { Gear, List, Wrench, InfoCircle, CheckCircle } from 'react-bootstrap-icons';
import reactDom from 'react-dom';
import { useTranslation } from 'react-i18next';
import SweetAlerts from '../../components/SweetAlerts';
import SimpleHeader from '../../components/Headers/SimpleHeader';
// import AlertBox from '../../components/AlertBox';
import SalesInput from '../../components/SalesInput';
import client from '../../utils/client';
import {
  getIdAndHashForWebSale,
  getLanguage,
  getLoginUrl,
  getTokenData,
  isSuperAdmin
} from '../../utils/helpers';
import { Store } from '../../Store';
import WebSalePayouts from '../../components/WebSales/WebSalePayouts';
import WebSaleSettings from '../../components/WebSales/WebSaleSettings';

let products = [];
let script = '';
let webSalesLink = '';

const superAdmin = isSuperAdmin();
let golfclubName = '';

const WebSales = () => {
  const { state, dispatch } = useContext(Store);
  const { shown } = state.releaseInformation;

  const { t } = useTranslation();

  const [inProgress, setInProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [loginUrl, setLoginUrl] = useState('');
  const [showSetupInfo, setShowSetupInfo] = useState(false);
  const [settingsInfo, setSettingsInfo] = useState(null);
  const [sales, setSales] = useState(null);
  const [salesDataInvalid, setSalesDataValid] = useState(null);
  const [sale, setSale] = useState(null);
  const [saleLog, setSaleLog] = useState(null);

  useEffect(() => {
    document.body.classList.add('web-sales');
    setLoginUrl(getLoginUrl('web-sales'));
    setError(null);
    setSuccess(null);

    const data = getIdAndHashForWebSale();
    const language = getLanguage();
    const tokenData = getTokenData();

    // eslint-disable-next-line max-len
    script = `<script>\nvar golfamore = {clubId: ${data.id}, hash: '${data.hash}'};\nvar gmElm = document.getElementById('golfamore');\nvar returnUrl = encodeURIComponent(window.location.href);\nvar iframeElm = document.createElement('iframe');\niframeElm.src = 'https://www.golfamore.com/club-sales/?ga-clubId='+golfamore.clubId+'&ga-clubHash='+golfamore.hash+'&ga-parentUrl='+returnUrl;\niframeElm.width = '100%';\niframeElm.height = '1000px';\niframeElm.frameBorder = 0;\ngmElm.appendChild(iframeElm);\n</script>`;

    golfclubName = tokenData.name;

    const golfclub = tokenData.name?.replace(' ', '-');
    webSalesLink = `https://www.golfamore.com/${language}/web-form/${data.id}/${golfclub}/${data.hash}/`;

    return () => document.body.classList.remove('web-sales');
  }, []);

  useEffect(() => {
    (async () => {
      setInProgress(true);
      const [cmp, prods, sls, active] = await Promise.all([
        client('admin-gc/campaigns'),
        client('admin-gc/products'),
        client('admin-gc/web-sales/sales'),
        client('admin-gc/web-sales')
      ]);

      if (cmp.error || prods.error || sls.error) {
        setError(cmp.error?.message || prods.error?.message || sls.error?.message || 'Error');
        setInProgress(false);
        return;
      }

      products = prods.data;
      reactDom.unstable_batchedUpdates(() => {
        // setCampaign(cmp.data);
        setSales(sls.data);
        setIsActive(!!active.data);
        setInProgress(false);
      });

      updateUnHandledWebsales(sls.data);
    })();
  }, []);

  const updateUnHandledWebsales = (sls) => {
    const unHandledWebSales = sls.filter((s) => !s.complete).length;
    dispatch({ type: 'UNHANDLED_WEB_SALES', payload: unHandledWebSales });
  };

  const activateWebSale = async () => {
    setInProgress(true);
    const { error: err } = await client('admin-gc/web-sales/activate', {}, 'post');
    setInProgress(false);

    if (!err) {
      setIsActive(true);
    }
  };

  const showSettings = async () => {
    setInProgress('fetching-settings');
    setSettingsInfo({});
    setError(null);
    setSuccess(null);
    const { data: settings } = await client('admin-gc/web-sales/settings');
    setInProgress(null);
    setSettingsInfo(settings);
    // reset(settings);
  };

  const onClickStatus = async (salesId) => {
    setError(null);
    const sl = sales.find((s) => s.id === salesId);

    const product = products
      .map((p) => ({
        ...p,
        subProducts: p.subProducts.find((s) => s.id === sl.productId)
      }))
      .find((p) => p.subProducts);

    const saleData = [...Array(sl.number).keys()].map((n) => ({
      ...sl,
      originalId: sl.id,
      cardNumber: '',
      digital: product?.subProducts?.digital,
      email: sl.email,
      id: `${sl.id}-${n}`,
      product: product?.subProducts?.name
        ? `${product?.name} - ${product?.subProducts.name}`
        : product?.name,
      productId: sl.productId,
      productCode: product?.code,
      key: n
    }));

    const shouldBtnsBeDisabled = saleData.filter((s) => !s.digital).length > 0;

    setError(null);
    setSuccess(null);
    setSalesDataValid(shouldBtnsBeDisabled);
    setSale(saleData);
  };

  const onUserChange = ({ name, value }, productId, id) => {
    const saleUpdated = sale.map((u) =>
      u.productId === productId && u.id === id ? { ...u, [name]: value } : { ...u }
    );
    const invalidData =
      saleUpdated.filter((u) => !u.email || (!u.digital && !u.cardNumber)).length > 0;

    setSalesDataValid(invalidData);
    setSale(saleUpdated);
  };

  const updateSale = async (action) => {
    setInProgress(`btn-${action}`);
    setError(null);
    setSuccess(null);

    if (action === 'cnl') {
      await handleCancelSale();
    } else {
      await handleUpdateSale(action);
    }

    setInProgress(null);
  };

  const resetSale = async (saleId) => {
    setInProgress(`btn-reset`);
    setError(null);
    setSuccess(null);

    const { data: updatedSale, error: salesError } = await client(
      `admin-gc/web-sales/sales/${saleId}`,
      { body: { action: 'reset' } }
    );
    if (salesError) {
      setError(salesError.message);
    } else {
      const sls = sales.map((s) => (s.id === updatedSale.id ? updatedSale : { ...s }));
      setSales(sls);
      setSuccess(updatedSale.id);
      setSaleLog(null);
    }
    setInProgress(null);
  };

  const handleCancelSale = async () => {
    const { data: deletedSale, error: deleteSaleError } = await client(
      `admin-gc/web-sales/sales/${sale[0].originalId}`,
      {},
      'delete'
    );

    if (deleteSaleError) {
      setError(deleteSaleError.message);
    } else {
      const sls = sales.map((s) => (s.id === deletedSale.id ? deletedSale : { ...s }));
      setSales(sls);
      setSuccess(deletedSale.id);
      setSale(null);
      updateUnHandledWebsales(sls);
    }
  };

  const handleUpdateSale = async (action) => {
    const data = {
      action,
      sales: sale.map((s) => ({ email: s.email, cardNumber: s.cardNumber }))
    };

    const { data: updatedSale, error: salesError } = await client(
      `admin-gc/web-sales/sales/${sale[0].originalId}`,
      { body: data }
    );

    if (salesError) {
      setError(salesError.message);
    } else {
      const sls = sales.map((s) => (s.id === updatedSale.id ? updatedSale : { ...s }));
      setSales(sls);
      setSuccess(updatedSale.id);
      setSale(null);
      updateUnHandledWebsales(sls);
    }
  };

  const showSalesLog = async (salesId) => {
    const { data: slsLog } = await client(`admin-gc/web-sales/sales-log?sales-id=${salesId}`);
    setError(null);
    setSuccess(null);
    setSaleLog({ id: salesId, logs: slsLog });
  };

  const creditSale = async (salesId) => {
    setInProgress('credit');
    setError(null);
    setSuccess(null);

    const { data: creditedSale, error: creditError } = await client(
      `admin-gc/web-sales/sales/${salesId}`,
      {},
      'delete'
    );

    if (creditError) {
      setError(creditError.message);
    } else {
      const sls = sales.map((s) => (s.id === creditedSale.id ? creditedSale : { ...s }));
      setSales(sls);
      setSuccess(creditSale.id);
      setSaleLog(null);
    }

    setInProgress(null);
  };

  const plusSale = sale?.[0]?.productCode === 'GHPL';
  const digitaSale = sale?.[0]?.digital;
  const digitalRegistration = plusSale || digitaSale;

  // console.log('webSalesReports', webSalesReports);

  return (
    <>
      <SimpleHeader name={t('web-sales.title', 'Web-försäljning')} />
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <div className="card-wrapper">
              {/* <Card>
                <CardHeader>
                  <h3 className="mb-0">Maintenance in progress</h3>
                </CardHeader>
                <CardBody>Please check back in a while</CardBody>
              </Card> */}

              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('web-sales.sub-title', 'Setup web-formulär')}</h3>
                </CardHeader>
                <CardBody>
                  {isActive && (
                    <Row>
                      <Col>
                        <Button
                          color="default"
                          className="mb-2"
                          onClick={() => setShowSetupInfo(true)}
                        >
                          <Wrench className="mr-2" />
                          {t('web-sales.setup', 'Setup för att komma igång')}
                        </Button>
                        <Button color="default" onClick={showSettings} className="mb-2 ml-0">
                          <Gear className="mr-2" />
                          {t('web-sales.change-settings', 'Ändra inställningar')}
                        </Button>
                      </Col>
                    </Row>
                  )}

                  {!inProgress && !isActive && (
                    <>
                      <p>
                        {t(
                          'web-sales.get-started',
                          'Kontakta Golfamore för att komma igång med web-försäljning på er hemsida'
                        )}
                      </p>
                      {superAdmin && (
                        <Button color="default" onClick={activateWebSale} className="mb-2 ml-0">
                          Super admin - Activate web sale
                        </Button>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('web-sales.sales', 'Försäljningar')}</h3>
                </CardHeader>
                <CardBody>
                  {error && <Alert color="danger">{error}</Alert>}
                  <Row>
                    <Col>
                      <Alert color="secondary" className="mt-0 mb-4">
                        <InfoCircle style={{ fontSize: '2em' }} className="mr-2" />
                        {t('web-sales.first-time-information')}
                      </Alert>
                    </Col>
                  </Row>
                  {inProgress && <Spinner />}

                  <WebSalePayouts />

                  <Table striped responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>{t('web-sales.id', 'Id')}</th>
                        <th>{t('default.date')}</th>
                        <th>{t('web-sales.sales')}</th>
                        <th>{t('web-sales.products', 'Produkter')}</th>
                        <th>{t('web-sales.delivery-type', 'Leveranssätt')}</th>
                        <th>{t('web-sales.payment-type', 'Betalning')}</th>
                        <th>{t('web-sales.payment-paid', 'Betald')}</th>
                        <th>{t('web-sales.handel', 'Hantera')}</th>
                        <th>{t('web-sales.invoiced', 'Fakturerad')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales?.map((s) => (
                        <tr key={s.id} className={s.id === success ? 'tr-success' : ''}>
                          <td>
                            <Button
                              color="link"
                              className="only-icon"
                              onClick={() => showSalesLog(s.id)}
                            >
                              <List />
                            </Button>
                            {s.id}
                          </td>
                          <td>{s.date}</td>
                          <td>{s.name}</td>
                          <td>{`${s.number} ${s.productCode}`}</td>
                          <td>{s.delivery ? t(`web-sales.statuses-${s.delivery}`) : s.delivery}</td>
                          <td>{t(`web-sales.payment-type-${s.payment}`) || s.payment}</td>
                          <td>
                            {s.paid || s.paidClub ? <CheckCircle /> : ''}{' '}
                            {s.paidClub ? t('web-sales.paid-club', 'Klubb') : ''}
                          </td>
                          <td className="text-align-center">
                            {s.complete ? (
                              <>{s.status && t(`web-sales.statuses-${s.status}`)}</>
                            ) : s.clubHandle ? (
                              <>
                                <Button
                                  onClick={() => onClickStatus(s.id)}
                                  size="sm"
                                  color="success"
                                >
                                  {t('default.update')}
                                </Button>
                              </>
                            ) : (
                              'Pågående'
                            )}
                          </td>
                          <td>{s.selfInvoiced}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {!shown && (
        <SweetAlerts
          type="info"
          title=""
          onConfirm={() => dispatch({ type: 'RELEASE_INFORMATION_SHOWN', payload: true })}
        >
          {t(
            'web-sales.first-time-information',
            // eslint-disable-next-line max-len
            'OBS - VIKTIGT! När ni hanterar en webb-försäljning på denna sida kommer den per automatik registreras som en försäljning och om det är digitala kort som beställts skickas dem direkt. Finns det en pågående kampanj registreras försäljningen även mot den. Ni ska alltså INTE som tidigare hantera webb-försäljningar på fler ställen med olika registreringar.'
          )}
        </SweetAlerts>
      )}

      {showSetupInfo && (
        <Modal isOpen centered>
          <ModalHeader>
            {t('web-sales.get-started-title', 'Kom igång med web-försäljning')}
          </ModalHeader>
          <ModalBody>
            {superAdmin && (
              <>
                <h4>Länk försäljning på Golfamores hemsida</h4>
                <p>
                  <code>{webSalesLink}</code>
                </p>
              </>
            )}
            <p>
              {t(
                'web-sales.get-started-text',
                // eslint-disable-next-line max-len
                'För att implementera web-försäljning på er hemsida behöver ni genomföra nedanstående steg. Observera att detta bör genomföras av er webmaster eller motsvarande som hanterar er hemsida.'
              )}
            </p>
            <p>
              1.{' '}
              {t(
                'web-sales.get-started-p1',
                'Lägg till följande tagg där ni vill formuläret ska synas'
              )}
              <br />
              <code>&lt;div id="golfamore"&gt;&lt;/div&gt;</code>
            </p>
            <p>
              2.{' '}
              {t(
                'web-sales.get-started-p2',
                'Lägg till följande script tag sist på sidan, precis innan'
              )}
              <code>&lt;/body&gt;</code> taggen
            </p>
            <pre>
              <code>{script}</code>
            </pre>
            <p>
              3.{' '}
              {t(
                'web-sales.get-started-p3',
                // eslint-disable-next-line max-len
                'Kontrollera att formuläret syns på er hemsida och gör en testbeställning. Fungerar allt som det ska kommer beställningen upp på denna sidan efter att ni uppdaterat.'
              )}
            </p>
            <p>
              4.{' '}
              {t(
                'web-sales.get-started-p4',
                'Klart. Gör eventuella ändringar under "Ändra inställningar"'
              )}
            </p>
            <hr />
            <p>
              {t(
                'web-sales.get-started-external-link',
                // eslint-disable-next-line max-len
                'Ni kan använda följa länk för att dela med er av setupen nedan. Observera att länken ger direkttillgång till ert admin system så var försiktiga med vem ni delar den med.'
              )}
            </p>
            <code>{loginUrl}</code>
          </ModalBody>
          <ModalFooter>
            <Button color="default" onClick={() => setShowSetupInfo(false)}>
              {t('default.close')}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {settingsInfo && (
        <WebSaleSettings
          settings={settingsInfo}
          superAdmin={superAdmin}
          golfclubName={golfclubName}
          onClose={() => setSettingsInfo(null)}
        />
      )}

      {sale && (
        <Modal isOpen centered size="lg">
          <ModalHeader>{t('web-sales.update-sale-title', 'Uppdatera försäljningen')}</ModalHeader>
          <ModalBody>
            <Form>
              {inProgress === 'fetching-settings' && <Spinner />}
              <Row>
                <Col className="col-sm-6">
                  <FormGroup>
                    <Label>{t('web-sales.update-sale-customer', 'Kund')}</Label>
                    <RInput
                      value={`${sale[0].golfId ? `${sale[0].golfId} - ` : ''} ${sale[0].name}`}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col className="col-sm-6">
                  <FormGroup>
                    <Label>{t('default.address')}</Label>
                    <RInput value={sale[0].address?.replace('<br>', ', ')} disabled />
                  </FormGroup>
                </Col>
                <Col className="col-sm-6">
                  <FormGroup>
                    <Label>{t('default.email')}</Label>
                    <RInput value={sale[0].email} disabled />
                  </FormGroup>
                </Col>
                <Col className="col-sm-6">
                  <FormGroup>
                    <Label>{t('default.mobile')}</Label>
                    <RInput value={sale[0].mobile} disabled />
                  </FormGroup>
                </Col>
                <Col className="col-sm-6">
                  <FormGroup>
                    <Label>{t('default.product')}</Label>
                    <RInput value={`${sale[0].number}st ${sale[0].product || 'N/A'}`} disabled />
                  </FormGroup>
                </Col>
                <Col className="col-sm-6">
                  <FormGroup>
                    <Label>{t('web-sales.delivery-type')}</Label>
                    <RInput value={t(`web-sales.statuses-${sale[0].delivery}`)} disabled />
                  </FormGroup>
                </Col>
              </Row>

              {!sale[0].product ? (
                <Alert color="warning">
                  {t(
                    'web-sales.product-not-found',
                    // eslint-disable-next-line max-len
                    'Produkten kan inte hittas. Försäljningen går inte att hantera automatiskt. Ni kan fortfarande registrera försäljningen som "Manuellt hanterad" och sen registrera den på egen hand under "Sälj Golfamore".'
                  )}
                </Alert>
              ) : (
                <>
                  <h5 className="mt-3 font-weight-bold">{sale[0].product}</h5>
                  {sale.map((s) => (
                    <SalesInput key={s.id} data={s} onChange={onUserChange} />
                  ))}
                </>
              )}
              {sale[0].campaignName && (
                <Alert color="success" className="mt-3">
                  {t('web-sales.campaign-registered', 'Försäljningen är registrerad på kampanjen')}:
                   {sale[0].campaignName}
                </Alert>
              )}
              {digitaSale && (
                <Alert color="success" className="mt-3">
                  {t(
                    'web-sales.register-help',
                    // eslint-disable-next-line max-len
                    // 'Digitalt kort, kontrollera att e-posten {{saleEmail}} är korrekt, klicka sen på "Registrera" försäljning. Ett e-post meddelande med ett digitalt kortnummer kommer skickas till kunden.'
                    { saleEmail: sale[0].email }
                  )}
                </Alert>
              )}

              {error && <Alert color="danger">{error}</Alert>}
            </Form>
          </ModalBody>
          <ModalFooter>
            {/* <h4>Har ni redan hanterat försäljningen?</h4>             */}
            <UncontrolledButtonDropdown disabled={!!inProgress}>
              <DropdownToggle caret color="primary">
                {inProgress && <Spinner size="sm" />}
                {t('web-sales.register-sale-handle', 'Hantera')}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => updateSale('man')}>
                  {t('web-sales.register-sale-man', 'Manuellt hanterad')}
                  <br />
                  <span className="small">
                    {t(
                      'web-sales.register-sale-man-help',
                      'Status uppdateras men ingen försäljning registreras.'
                    )}
                  </span>
                </DropdownItem>
                <DropdownItem divider />
                {digitalRegistration ? (
                  <DropdownItem onClick={() => updateSale('e-sent')} disabled={!!salesDataInvalid}>
                    {t('web-sales.register-sale', 'Registrera försäljning')}
                  </DropdownItem>
                ) : (
                  <>
                    <DropdownItem onClick={() => updateSale('pud')} disabled={!!salesDataInvalid}>
                      {t('web-sales.register-sale-fetched', 'Registrera som hämtad')}
                    </DropdownItem>
                    <DropdownItem onClick={() => updateSale('sent')} disabled={!!salesDataInvalid}>
                      {t('web-sales.register-sale-sent', 'Registrera som skickad')}
                    </DropdownItem>
                    <DropdownItem divider />
                  </>
                )}
                <DropdownItem onClick={() => updateSale('cnl')} disabled={inProgress}>
                  {t('web-sales.register-sale-cancel', 'Avbryt beställning')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledButtonDropdown>
            <Button color="danger" onClick={() => setSale(null)}>
              {t('default.close')}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {saleLog && (
        <Modal isOpen centered size="lg">
          <ModalHeader toggle={() => setSaleLog(null)}>{t('web-sales.log', 'Log')}</ModalHeader>
          <ModalBody>
            <Table>
              <thead className="thead-light">
                <tr>
                  <th>{t('default.date')}</th>
                  <th>{t('web-sales.status', 'Status')}</th>
                </tr>
              </thead>
              <tbody>
                {saleLog.logs.map((s) => (
                  <Fragment key={s.id}>
                    <tr>
                      <td>{s.date}</td>
                      <td>
                        {t(`web-sales.statuses-${s.status}`)}{' '}
                        {s.price && `${s.price} ${s.currency}`}
                      </td>
                    </tr>
                    {s.status === 'add' && (
                      <>
                        <tr>
                          <td className="pt-0 pb-1" style={{ borderTop: 0 }}>
                            <b>{t('web-sales.products')}:</b> {s.qty} {s.productName}
                          </td>
                        </tr>
                        <tr>
                          <td className="pt-0 pb-1" style={{ borderTop: 0 }}>
                            <b>{t('web-sales.price')}:</b> {s.priceCustomer} {s.currency} (
                            {s.priceCustomer * s.qty} {s.currency})
                          </td>
                        </tr>
                        {s.campaignName && (
                          <tr>
                            <td className="pt-0" style={{ borderTop: 0, textWrap: 'balance' }}>
                              <b>{t('web-sales.campaign')}:</b> {s.campaignName}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                    {['pmt', 'pmtp'].includes(s.status) && (
                      <>
                        <tr>
                          <td className="pt-0 pb-1" style={{ borderTop: 0 }}>
                            <b>{t('web-sales.payment')}:</b>{' '}
                            {t(`web-sales.payment-type-${s.payment}`) || s.payment}
                          </td>
                        </tr>
                        <tr>
                          <td className="pt-0" style={{ borderTop: 0 }}>
                            <b>{t('web-sales.payment-id')}:</b> {s.paymentId}
                          </td>
                        </tr>
                      </>
                    )}
                    {['cred'].includes(s.status) && (
                      <tr>
                        <td className="pt-0" style={{ borderTop: 0 }}>
                          <b>{t('web-sales.credit-id')}:</b> {s.paymentId}
                        </td>
                      </tr>
                    )}
                    {s.cardNrs && (
                      <>
                        <tr>
                          <td className="pt-0 pb-1" style={{ borderTop: 0 }}>
                            <b>{t('web-sales.card-nr', 'Kortnummer')}:</b> {s.cardNrs.join(', ')}
                          </td>
                        </tr>
                        <tr>
                          <td className="pt-0" style={{ borderTop: 0 }}>
                            {s.status === 'e-sent' && (
                              <>
                                <b>{t('default.email', 'E-post')}:</b> {s.email}
                              </>
                            )}
                            {s.status === 'sent' && (
                              <>
                                <b>{t('default.address', 'Address')}:</b> {s.address}
                              </>
                            )}
                          </td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                ))}
                {!!saleLog.logs[0].canBeCredited && (
                  <tr className="text-right">
                    <td colSpan={2} className="pt-5 pb-0">
                      <Button color="danger" onClick={() => creditSale(saleLog.id)}>
                        {inProgress === 'credit' && <Spinner size="sm" />}{' '}
                        {t('web-sales.credit-order')}
                      </Button>
                    </td>
                  </tr>
                )}
                {saleLog.logs[saleLog.logs.length - 1]?.status === 'man' && (
                  <tr className="text-right">
                    <td colSpan={2} className="pt-5 pb-0">
                      <Button
                        color="success"
                        onClick={() => resetSale(saleLog.id)}
                        disabled={!!inProgress}
                      >
                        {inProgress === 'btn-reset' && <Spinner size="sm" />}{' '}
                        {t('web-sales.reset-status')}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default WebSales;
